import { GoogleClassroomClient } from "@/services/GoogleClassroom";
import { GoogleOAuthClient } from "@/services/GoogleOAuth";
import { FirestoreClient } from "@/services/FirestoreClient";
import {
  PupilLessonProgress,
  UpsertPupilLessonProgressArgs,
  UPDATABLE_SUBMISSION_STATES,
  upsertPupilLessonProgressArgsSchema,
  postSubmissionStateSchema,
  PostSubmissionState,
} from "@/types/services";
import { OakGoogleClassroomException, ErrorSeverity } from "@/errors";

export class OakPupilProgress {
  constructor(
    private readonly baseUrl: string,
    private readonly firestoreClient: FirestoreClient,
  ) {}

  /**
   * Gets the add-on context from Google Classroom, which includes the submissionId
   * for the current student.
   * @param courseId - The Google Classroom course identifier.
   * @param itemId - The courseWork item identifier.
   * @param attachmentId - The add-on attachment identifier.
   * @param oauthClient - An authenticated OAuth client for the current pupil.
   */
  public async getAddOnContext(
    courseId: string,
    itemId: string,
    attachmentId: string,
    oauthClient: GoogleOAuthClient,
  ) {
    const classroomClient = new GoogleClassroomClient(
      this.baseUrl,
      oauthClient,
    );
    return classroomClient.getAddOnContext({ courseId, itemId, attachmentId });
  }

  public async getPostSubmissionState(
    courseId: string,
    itemId: string,
    attachmentId: string,
    submissionId: string,
    teacherOAuthClient: GoogleOAuthClient,
  ): Promise<string> {
    const classroomClient = new GoogleClassroomClient(
      this.baseUrl,
      teacherOAuthClient,
    );
    const submission = await classroomClient.fetchPupilResponse({
      courseId: courseId,
      itemId: itemId,
      attachmentId: attachmentId,
      submissionId: submissionId,
    });
    const state =
      submission.postSubmissionState ??
      PostSubmissionState.SUBMISSION_STATE_UNSPECIFIED;

    const validatedState = postSubmissionStateSchema.parse(state);

    const existing = await this.firestoreClient.getPupilLessonProgress(
      submissionId,
      attachmentId,
      itemId,
    );

    if (existing) {
      const validatedArgs = upsertPupilLessonProgressArgsSchema.parse(existing);

      const now = new Date().toISOString();
      const progressData: PupilLessonProgress = {
        ...validatedArgs,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
        postSubmissionState:
          validatedState ??
          existing?.postSubmissionState ??
          PostSubmissionState.SUBMISSION_STATE_UNSPECIFIED,
      };

      await this.firestoreClient.upsertPupilLessonProgress(progressData);
    }

    return state;
  }

  /**
   * Persists pupil lesson progress to Firestore and, when the exit quiz
   * is newly completed, increments pointsEarned on the Google Classroom submission
   * using the teacher's OAuth credentials.
   * @param args - The pupil lesson progress data to upsert.
   * @param teacherOAuthClient - An OAuth client authenticated with the teacher's credentials, used for grade submission.
   * @returns The persisted pupil lesson progress record.
   */
  public async upsertPupilLessonProgress(
    args: UpsertPupilLessonProgressArgs,
    teacherOAuthClient?: GoogleOAuthClient,
  ): Promise<PupilLessonProgress> {
    let validatedState;
    if (teacherOAuthClient) {
      const classroomClient = new GoogleClassroomClient(
        this.baseUrl,
        teacherOAuthClient,
      );
      const submission = await classroomClient.fetchPupilResponse({
        courseId: args.courseId,
        itemId: args.itemId,
        attachmentId: args.attachmentId,
        submissionId: args.submissionId,
      });
      const state =
        submission.postSubmissionState ??
        PostSubmissionState.SUBMISSION_STATE_UNSPECIFIED;
      validatedState = postSubmissionStateSchema.parse(state);
    }

    const validatedArgs = upsertPupilLessonProgressArgsSchema.parse(args);

    const existing = await this.firestoreClient.getPupilLessonProgress(
      validatedArgs.submissionId,
      validatedArgs.attachmentId,
      validatedArgs.itemId,
    );

    const now = new Date().toISOString();
    const progressData: PupilLessonProgress = {
      ...existing,
      ...validatedArgs,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      postSubmissionState:
        validatedState ??
        existing?.postSubmissionState ??
        PostSubmissionState.SUBMISSION_STATE_UNSPECIFIED,
    };
    const isComplete =
      validatedState === PostSubmissionState.RETURNED ||
      validatedState === PostSubmissionState.TURNED_IN;
    if (!isComplete) {
      await this.firestoreClient.upsertPupilLessonProgress(progressData);
    }

    // If exit quiz is newly completed, update GC pointsEarned using teacher credentials
    if (
      validatedArgs.exitQuiz?.isComplete &&
      !existing?.exitQuiz?.isComplete &&
      teacherOAuthClient &&
      !isComplete
    ) {
      await this.submitPointsEarned(validatedArgs, teacherOAuthClient);
    }

    return progressData;
  }

  /**
   * Reads existing pointsEarned from GC, adds exit quiz grade, and patches the submission.
   * Throws SUBMISSION_NOT_UPDATABLE if the submission state doesn't allow updates.
   * @param args - The pupil lesson progress data containing exit quiz grade.
   * @param teacherOAuthClient - An OAuth client authenticated with the teacher's credentials.
   */
  private async submitPointsEarned(
    args: UpsertPupilLessonProgressArgs,
    teacherOAuthClient: GoogleOAuthClient,
  ): Promise<void> {
    const classroomClient = new GoogleClassroomClient(
      this.baseUrl,
      teacherOAuthClient,
    );

    const submission = await classroomClient.fetchPupilResponse({
      courseId: args.courseId,
      itemId: args.itemId,
      attachmentId: args.attachmentId,
      submissionId: args.submissionId,
    });

    const state =
      submission.postSubmissionState ??
      PostSubmissionState.SUBMISSION_STATE_UNSPECIFIED;
    const canUpdate = (
      UPDATABLE_SUBMISSION_STATES as readonly string[]
    ).includes(state);

    if (!canUpdate) {
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Submission is not in an updatable state",
        "submitPointsEarned",
        {
          submissionId: args.submissionId,
          submissionState: state,
        },
        undefined,
        ErrorSeverity.Warning,
      );
    }

    const currentPointsEarned = submission.pointsEarned ?? 0;
    const exitQuizGrade = args.exitQuiz?.grade ?? 0;
    const newPointsEarned = currentPointsEarned + exitQuizGrade;

    await classroomClient.submitPupilResponse({
      courseId: args.courseId,
      itemId: args.itemId,
      attachmentId: args.attachmentId,
      submissionId: args.submissionId,
      pointsEarned: newPointsEarned,
    });
  }

  /**
   * Retrieves pupil lesson progress from Firestore.
   * @param submissionId - The Google Classroom submission identifier.
   * @returns The pupil lesson progress record, or null if not found.
   */
  public async getPupilLessonProgress(
    submissionId: string,
    attachmentId: string,
    itemId: string,
  ): Promise<PupilLessonProgress | null> {
    return this.firestoreClient.getPupilLessonProgress(
      submissionId,
      attachmentId,
      itemId,
    );
  }
}
